import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../auth/AuthProvider";
import { AppShell } from "../components/AppShell";
import { CompanyLogo } from "../components/CompanyLogo";
import { useCompanies, useWhoAmI } from "../lib/api";
import { frappe } from "../lib/frappe";
import { ROLE_LABEL_KEYS } from "../lib/auth";
import { isRootCompany } from "../lib/company";
import { NAV, canSee, entityLabel, modulesFor, type NavModule } from "../lib/nav";

/** Test and runbook hooks kept on the links that carry them (e2e scripts, screenshots); one element per hook, matched on the exact route. */
const HOOKS: Record<string, string> = { "/orders": "data-orders-link", "/procure": "data-procure-link", "/pos": "data-pos-link", "/reports": "data-reports-link", "/process": "data-process-link", "/demo-metrics": "data-demo-metrics" };
const hook = (to: string) => { const h = HOOKS[to]; return h ? { [h]: "" } : {}; };

function ModuleCard({ module, wide = false }: { module: NavModule; wide?: boolean }) {
  const { t, i18n } = useTranslation();
  const shown = module.entities.slice(0, wide ? 12 : 8);
  return (
    <div data-module={module.key} className={`flex flex-col rounded-xl border border-brand-grey-light bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${wide ? "sm:col-span-2" : ""}`}>
      <Link to={module.to} {...hook(module.to)} className={`${module.color} rounded-t-xl px-5 py-3 text-white`}>
        <div className="font-heading text-base font-semibold leading-tight">{t(module.label)}</div>
        <div className="mt-0.5 text-xs opacity-90">{t(module.desc)}</div>
      </Link>
      <div className="flex flex-1 flex-wrap content-start gap-x-3 gap-y-1 px-5 py-3 text-xs">
        {shown.map((e) => (
          <Link key={e.key} to={e.to} {...hook(e.to)} className="text-brand-charcoal hover:text-brand-primary hover:underline">{entityLabel(e, i18n.language, t)}</Link>
        ))}
        {module.entities.length > shown.length && <Link to={module.to} className="font-semibold text-brand-primary">+{module.entities.length - shown.length} {t("nav.more")}</Link>}
        {module.entities.length === 0 && <Link to={module.to} className="font-semibold text-brand-primary hover:underline">{t("nav.openModule")} ›</Link>}
      </div>
    </div>
  );
}

/** Landing hub (requirements 9.1, 9.2): My dashboard, the Executive cockpit, the business lines with their entities, ERP setup
 * and Administration — each card filtered by the person's realm roles; the lines outside the role are named, not opened. */
export default function Landing() {
  const { t } = useTranslation();
  const { identity } = useAuth();
  const whoami = useWhoAmI();
  const health = useQuery({ queryKey: ["health"], queryFn: () => frappe.call<{ app: string; version: string }>("onesphere.api.health") });
  const companies = useCompanies();
  // subsidiaries the caller may see (User Permissions apply): every company below the root of the tree
  const subsidiaries = (companies.data ?? []).filter((c) => !isRootCompany(c)).map((c) => c.name);
  const roles = identity?.roles ?? [];
  const visible = modulesFor(roles);
  const byKey = new Map(visible.map((m) => [m.key, m]));
  const business = visible.filter((m) => !m.system && m.key !== "executive");
  const hidden = NAV.filter((m) => !m.system && !canSee(m.roles, roles));
  const my = byKey.get("my");
  const executive = byKey.get("executive");
  const admin = byKey.get("admin");

  return (
    <AppShell>
      <section className="mx-auto max-w-7xl py-4" data-landing>
        <h1 className="font-heading text-3xl font-semibold text-brand-charcoal">
          {t("landing.welcome", { name: whoami.data?.full_name ?? identity?.name ?? "" })}
        </h1>
        <p className="mt-1 text-brand-grey">{t("landing.subtitle")}</p>

        <div className="mt-6 grid grid-cols-1 items-start gap-4 sm:grid-cols-2 lg:grid-cols-4" data-landing-top>
          {my && (
            <Link to={my.to} data-module="my" className={`${my.color} flex flex-col justify-between rounded-xl p-5 text-white shadow transition hover:-translate-y-0.5 hover:shadow-lg`}>
              <div>
                <div className="font-heading text-lg font-semibold">{t(my.label)}</div>
                <div className="mt-1 text-sm opacity-90">{t(my.desc)}</div>
              </div>
              <div className="mt-4 text-xs opacity-90">{identity?.name} · {roles.length > 0 ? roles.map((r) => t(ROLE_LABEL_KEYS[r] ?? r)).join(" · ") : t("my.noRoles")}</div>
            </Link>
          )}
          {executive && <div className="sm:col-span-1 lg:col-span-3"><ModuleCard module={executive} wide /></div>}
        </div>

        <h2 className="mt-8 text-[11px] font-semibold uppercase tracking-wide text-brand-grey">{t("nav.businessLines")}</h2>
        <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-landing-modules>
          {business.map((m) => <ModuleCard key={m.key} module={m} />)}
        </div>
        {hidden.length > 0 && (
          <p className="mt-3 text-xs text-brand-grey" data-landing-hidden>
            {t("nav.notInRole")}: {hidden.map((m) => t(m.label)).join(" · ")}
          </p>
        )}

        <h2 className="mt-8 text-[11px] font-semibold uppercase tracking-wide text-brand-grey">{t("nav.crossCutting")}</h2>
        <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-landing-system>
          {admin ? <div className="lg:col-span-4"><ModuleCard module={admin} wide /></div> : (
            <div className="rounded-xl border border-dashed border-brand-grey-light p-5 text-sm text-brand-grey lg:col-span-4" data-module="admin-locked">
              <div className="font-heading font-semibold text-brand-charcoal">{t("nav.admin.title")}</div>
              <div className="mt-1 text-xs">{t("nav.adminOnly")}</div>
            </div>
          )}
        </div>

        <section className="mt-8">
          <h2 className="text-[11px] font-semibold uppercase tracking-wide text-brand-grey">{t("landing.subsidiaries")}</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {subsidiaries.map((n) => (
              <CompanyLogo key={n} company={n} size={22} showName className="rounded-lg border border-brand-grey-light bg-white px-3 py-1.5 text-xs text-brand-charcoal" />
            ))}
          </div>
        </section>
        <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-brand-grey">
          <span>{health.isSuccess ? `onesphere ${health.data.version}` : health.isError ? t("landing.offline") : "…"}</span>
          {whoami.data && (
            <span>
              {t("landing.signedInAs")} <span className="font-semibold text-brand-charcoal">{whoami.data.user}</span>
              {whoami.data.onesphere_roles.length > 0 && <> · {whoami.data.onesphere_roles.join(", ")}</>}
            </span>
          )}
        </div>
      </section>
    </AppShell>
  );
}
